import { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Textarea, Loader, Modal, Text, FileInput, Input, Menu, ActionIcon, Badge, Select } from '@mantine/core';
import PageHeader from '@/components/page-header';
import { usePayment, useUpdatePayment, useDeletePayment, useUnpaidServiceBookings } from '@/hooks/api-hooks';
import { Payment, PaymentReservation, PaymentServiceBooking, Reservation, ServiceBooking } from '@turizi/shared';
import { SkeletonList, MoneyNumberInput } from '@/components/snippets';
import { notify } from '@/lib/notify';
import { navigate, PAYMENTS_INCOME_PATH, PAYMENTS_OUTCOME_PATH, RESERVATIONS_PATH, BUSINESSES_PATH } from '@/lib/navigation';
import { useDisclosure } from '@mantine/hooks';
import { setWebTitle, formatPrice, formatDayLong } from '@/lib/format-utils';
import {
    IconArrowRight,
    IconArrowLeft,
    IconArrowDownLeft,
    IconArrowUpRight,
    IconTrash,
    IconX,
    IconUpload,
    IconFileText,
    IconPlus,
    IconDotsVertical,
    IconPencil,
    IconLink,
    IconNotes,
    IconLinkPlus,
    IconWand
} from '@tabler/icons-react';
import api from '@/services/api-service';
import { DateInput } from '@mantine/dates';
import ShowMetadata from '@/components/show-metadata';
import { localTenant } from '@/services/session-service';
import { TagIcon } from '@/lib/tag-utils';
import { ServiceTitleStatic } from '@/components/services/service-title';
import AutoAssignPaymentsModal from '@/components/auto-assign-payments-modal';
import PaymentRetentionsSection from '@/components/payment-retentions-section';

function LinkedReservationRow({
    reservation,
    currency,
    onAmountChange,
    onUnlink,
}: {
    reservation: Partial<Reservation> & { id: number; PaymentReservation?: PaymentReservation; payment_reservation?: PaymentReservation };
    currency?: string;
    onAmountChange: (id: number, amount: number) => void;
    onUnlink: (id: number) => void;
}) {
    const { t } = useTranslation();
    const linkAmt = reservation.PaymentReservation?.amount ?? reservation.payment_reservation?.amount ?? 0;
    const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
    const [tempAmount, setTempAmount] = useState<number>(linkAmt);

    useEffect(() => {
        setTempAmount(linkAmt);
    }, [linkAmt]);

    const isAmountInvalid = tempAmount <= 0;

    const handleSave = () => {
        if (isAmountInvalid) return;
        onAmountChange(reservation.id, tempAmount);
        closeEditModal();
    };

    return (
        <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50/80 border border-gray-200/70 gap-3 hover:bg-slate-100/60 transition-colors">
            <div className="inline-flex items-center gap-2.5 min-w-0">
                <Link
                    to={`${RESERVATIONS_PATH}/${reservation.id}`}
                    className="inline-flex items-center gap-1.5 hover:underline text-blue-700 font-bold text-xs bg-blue-50 px-2.5 py-1 rounded-full border border-blue-200 shrink-0"
                >
                    <IconFileText size={13} />
                    #{reservation.id}
                </Link>
                {reservation.name && (
                    <span className="text-xs font-semibold text-gray-700">
                        {reservation.name}
                    </span>
                )}
            </div>

            <div className="flex items-center gap-2 shrink-0">
                <div className="flex items-center gap-1.5 bg-white px-2.5 py-1 rounded-lg border border-slate-200/90 shadow-2xs">
                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">{t('applied_amount')}:</span>
                    <span className="text-xs font-extrabold text-slate-800">
                        ${formatPrice(linkAmt)} <span className="text-[9px] text-slate-400 font-bold uppercase">{currency}</span>
                    </span>
                </div>

                <Menu shadow="md" width={160} position="bottom-end">
                    <Menu.Target>
                        <ActionIcon variant="subtle" color="gray" radius="md" size="sm">
                            <IconDotsVertical size={16} />
                        </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <Menu.Item
                            leftSection={<IconPencil size={14} />}
                            onClick={openEditModal}
                        >
                            {t('edit')}
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                            color="red"
                            leftSection={<IconTrash size={14} />}
                            onClick={() => onUnlink(reservation.id)}
                        >
                            {t('delete_link')}
                        </Menu.Item>
                    </Menu.Dropdown>
                </Menu>

                <Modal
                    opened={editModalOpened}
                    onClose={closeEditModal}
                    title={
                        <span className="font-bold text-sm text-slate-800">
                            {t('edit_applied_amount')} #{reservation.id}
                        </span>
                    }
                    size="sm"
                    centered
                >
                    <div className="space-y-4 py-1">
                        <div>
                            <span className="text-xs font-semibold text-slate-600 block mb-1">
                                {t('applied_amount')} ({currency})
                            </span>
                            <MoneyNumberInput
                                amount={tempAmount}
                                onChange={(val) => setTempAmount(val || 0)}
                                error={isAmountInvalid ? t('amount_must_be_greater_than_zero') : undefined}
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                            <Button
                                size="xs"
                                variant="subtle"
                                color="gray"
                                onClick={() => {
                                    setTempAmount(linkAmt);
                                    closeEditModal();
                                }}
                            >
                                {t('cancel')}
                            </Button>
                            <Button
                                size="xs"
                                color="blue"
                                disabled={isAmountInvalid}
                                onClick={handleSave}
                            >
                                {t('save')}
                            </Button>
                        </div>
                    </div>
                </Modal>
            </div>
        </div>
    );
}

function LinkReservationModal({
    opened,
    onClose,
    defaultAmount,
    currency,
    existingIds,
    onConfirm,
}: {
    opened: boolean;
    onClose: () => void;
    defaultAmount: number;
    currency?: string;
    existingIds: number[];
    onConfirm: (resId: number, amount: number) => void;
}) {
    const { t } = useTranslation();
    const [resIdInput, setResIdInput] = useState('');
    const [amountInput, setAmountInput] = useState<number>(defaultAmount);

    useEffect(() => {
        if (opened) {
            setResIdInput('');
            setAmountInput(defaultAmount);
        }
    }, [opened, defaultAmount]);

    const isAmountInvalid = amountInput <= 0;
    const isSaveDisabled = !resIdInput.trim() || isAmountInvalid;

    const handleConfirm = () => {
        if (isSaveDisabled) return;
        const parsedId = parseInt(resIdInput.trim(), 10);
        if (isNaN(parsedId) || parsedId <= 0) {
            notify.error(t('error'), t('enter_reservation_id'));
            return;
        }
        if (existingIds.includes(parsedId)) {
            notify.error(t('error'), t('reservation_already_linked'));
            return;
        }
        onConfirm(parsedId, amountInput || 0);
        onClose();
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={
                <div className="flex items-center gap-2 font-bold text-sm text-slate-800">
                    <IconLink size={16} className="text-blue-600" />
                    <span>{t('link_reservation')}</span>
                </div>
            }
            size="sm"
            centered
        >
            <div className="space-y-4 py-1">
                <div>
                    <span className="text-xs font-semibold text-slate-600 block mb-1">
                        {t('enter_reservation_id')}
                    </span>
                    <Input
                        placeholder="e.g. 1234"
                        size="xs"
                        type="number"
                        value={resIdInput}
                        onChange={(e) => setResIdInput(e.target.value)}
                        className="font-medium"
                    />
                </div>
                <div>
                    <span className="text-xs font-semibold text-slate-600 block mb-1">
                        {t('amount_to_link')} ({currency})
                    </span>
                    <MoneyNumberInput
                        amount={amountInput}
                        onChange={(val) => setAmountInput(val || 0)}
                        error={isAmountInvalid ? t('amount_must_be_greater_than_zero') : undefined}
                    />
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                    <Button
                        size="xs"
                        variant="subtle"
                        color="gray"
                        onClick={onClose}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        size="xs"
                        color="blue"
                        disabled={isSaveDisabled}
                        onClick={handleConfirm}
                    >
                        {t('link_reservation')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

function LinkedServiceBookingRow({
    booking,
    currency,
    onAmountChange,
    onUnlink,
}: {
    booking: Partial<ServiceBooking> & { id: number; PaymentServiceBooking?: PaymentServiceBooking; payment_service_booking?: PaymentServiceBooking };
    currency?: string;
    onAmountChange: (id: number, amount: number) => void;
    onUnlink: (id: number) => void;
}) {
    const { t } = useTranslation();
    const linkAmt = booking.PaymentServiceBooking?.amount ?? booking.payment_service_booking?.amount ?? 0;
    const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
    const [tempAmount, setTempAmount] = useState<number>(linkAmt);

    useEffect(() => {
        setTempAmount(linkAmt);
    }, [linkAmt]);

    const isAmountInvalid = tempAmount <= 0;

    const handleSave = () => {
        if (isAmountInvalid) return;
        onAmountChange(booking.id, tempAmount);
        closeEditModal();
    };

    const service = booking.service || booking.service_data;

    return (
        <div className="p-3 rounded-xl bg-slate-50/80 border border-slate-200/70 space-y-2 hover:bg-slate-100/60 transition-colors">
            <div className="flex items-center justify-between gap-2">
                <div className="inline-flex items-center gap-2 min-w-0 flex-wrap">
                    <span className="font-bold text-xs text-slate-500">
                        #{booking.id}
                    </span>
                    {booking.reservation_id && (
                        <Link
                            to={`${RESERVATIONS_PATH}/${booking.reservation_id}`}
                            className="inline-flex items-center gap-1 hover:underline text-blue-700 font-bold text-xs bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200"
                        >
                            <IconFileText size={12} />
                            #{booking.reservation_id}
                        </Link>
                    )}
                    {service && (
                        <div className="text-xs font-semibold text-slate-800 flex items-center gap-1">
                            <TagIcon name={service.category} size="xs" noBackground hideText />
                            <span>{ServiceTitleStatic(service)}</span>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    <div className="flex items-center gap-1.5 bg-white px-2.5 py-1 rounded-lg border border-slate-200/90 shadow-2xs">
                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">{t('applied_amount')}:</span>
                        <span className="text-xs font-extrabold text-slate-800">
                            ${formatPrice(linkAmt)} <span className="text-[9px] text-slate-400 font-bold uppercase">{currency}</span>
                        </span>
                    </div>

                    <Menu shadow="md" width={160} position="bottom-end">
                        <Menu.Target>
                            <ActionIcon variant="subtle" color="gray" radius="md" size="sm">
                                <IconDotsVertical size={16} />
                            </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Menu.Item
                                leftSection={<IconPencil size={14} />}
                                onClick={openEditModal}
                            >
                                {t('edit')}
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item
                                color="red"
                                leftSection={<IconTrash size={14} />}
                                onClick={() => onUnlink(booking.id)}
                            >
                                {t('delete_link')}
                            </Menu.Item>
                        </Menu.Dropdown>
                    </Menu>
                </div>
            </div>

            <Modal
                opened={editModalOpened}
                onClose={closeEditModal}
                title={
                    <span className="font-bold text-sm text-slate-800">
                        {t('edit_applied_amount')} #{booking.id}
                    </span>
                }
                size="sm"
                centered
            >
                <div className="space-y-4 py-1">
                    <div>
                        <span className="text-xs font-semibold text-slate-600 block mb-1">
                            {t('applied_amount')} ({currency})
                        </span>
                        <MoneyNumberInput
                            amount={tempAmount}
                            onChange={(val) => setTempAmount(val)}
                            className="w-full"
                            error={isAmountInvalid ? t('amount_must_be_greater_than_zero') : undefined}
                        />
                    </div>
                    <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                        <Button size="xs" variant="subtle" color="gray" onClick={closeEditModal}>
                            {t('cancel')}
                        </Button>
                        <Button size="xs" color="blue" disabled={isAmountInvalid} onClick={handleSave}>
                            {t('save')}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

function LinkServiceBookingCostModal({
    opened,
    onClose,
    providerId,
    defaultAmount,
    currency,
    existingIds,
    onConfirm,
}: {
    opened: boolean;
    onClose: () => void;
    providerId?: number;
    defaultAmount: number;
    currency?: string;
    existingIds: number[];
    onConfirm: (booking: ServiceBooking, amount: number) => void;
}) {
    const { t } = useTranslation();
    const {
        data: response,
        isLoading
    } = useUnpaidServiceBookings(
        providerId,
        { enabled: opened && !!providerId }
    );
    const unpaidBookings = (response?.data || []).filter(b => !existingIds.includes(b.id));

    const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
    const [amount, setAmount] = useState<number>(defaultAmount);

    useEffect(() => {
        if (opened) {
            setAmount(defaultAmount);
            setSelectedBookingId(null);
        }
    }, [opened, defaultAmount]);

    const isAmountInvalid = amount <= 0;

    const handleConfirm = () => {
        if (!selectedBookingId || isAmountInvalid) return;
        const booking = unpaidBookings.find(b => String(b.id) === selectedBookingId);
        if (!booking) return;
        onConfirm(booking, amount);
        onClose();
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={
                <div className="flex items-center gap-2 font-bold text-sm text-slate-800">
                    <IconLink size={16} className="text-blue-600" />
                    <span>{t('reservations')}</span>
                </div>
            }
            size="md"
            centered
        >
            <div className="space-y-4 py-1">
                {isLoading ? (
                    <div className="flex justify-center py-6">
                        <Loader size="sm" />
                    </div>
                ) : unpaidBookings.length === 0 ? (
                    null
                ) : (
                    <>
                        <Select
                            label={t('select_booking')}
                            placeholder={t('select_booking')}
                            data={unpaidBookings.map(b => ({
                                value: String(b.id),
                                label: `#${b.id} - ${b.service?.name || b.service_data?.name || 'Service'} (${b.start_day || ''}) - ${b.money_out_currency || '$'} ${b.money_out_amount || 0}`
                            }))}
                            value={selectedBookingId}
                            onChange={(val) => {
                                setSelectedBookingId(val);
                                const selected = unpaidBookings.find(b => String(b.id) === val);
                                if (selected?.money_out_amount) {
                                    setAmount(Number(selected.money_out_amount));
                                }
                            }}
                            searchable
                        />

                        {selectedBookingId && (
                            <div>
                                <span className="text-xs font-semibold text-slate-600 block mb-1">
                                    {t('applied_amount')} ({currency})
                                </span>
                                <MoneyNumberInput
                                    amount={amount}
                                    onChange={(val) => setAmount(val || 0)}
                                    className="w-full"
                                    error={isAmountInvalid ? t('amount_must_be_greater_than_zero') : undefined}
                                />
                            </div>
                        )}
                    </>
                )}

                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                    <Button size="xs" variant="subtle" color="gray" onClick={onClose}>
                        {t('cancel')}
                    </Button>
                    <Button size="xs" color="blue" disabled={!selectedBookingId || isAmountInvalid} onClick={handleConfirm}>
                        {t('confirm')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

function EditNotesModal({
    opened,
    onClose,
    currentNotes,
    onSave,
}: {
    opened: boolean;
    onClose: () => void;
    currentNotes: string;
    onSave: (notes: string) => void;
}) {
    const { t } = useTranslation();
    const [notesInput, setNotesInput] = useState(currentNotes);

    useEffect(() => {
        if (opened) {
            setNotesInput(currentNotes || '');
        }
    }, [opened, currentNotes]);

    const handleSave = () => {
        onSave(notesInput);
        onClose();
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={
                <span className="font-bold text-sm text-slate-800">
                    {currentNotes ? t('edit_note') : t('add_note')}
                </span>
            }
            size="md"
            centered
        >
            <div className="space-y-4 py-1">
                <Textarea
                    placeholder={t('add_notes')}
                    minRows={5}
                    value={notesInput}
                    onChange={(e) => setNotesInput(e.currentTarget.value)}
                    className="text-gray-800 text-sm"
                />
                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                    <Button
                        size="xs"
                        variant="subtle"
                        color="gray"
                        onClick={onClose}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        size="xs"
                        color="blue"
                        onClick={handleSave}
                    >
                        {t('save')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

export default function EditPaymentPage() {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation();

    setWebTitle(t('payment') + ' #' + id);

    const {
        data: paymentData,
        isLoading: isLoadingPayment
    } = usePayment(id!);
    const updatePaymentMutation = useUpdatePayment();
    const deletePaymentMutation = useDeletePayment();

    const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
    const [linkModalOpened, { open: openLinkModal, close: closeLinkModal }] = useDisclosure(false);
    const [linkCostModalOpened, { open: openLinkCostModal, close: closeLinkCostModal }] = useDisclosure(false);
    const [notesModalOpened, { open: openNotesModal, close: closeNotesModal }] = useDisclosure(false);
    const [autoAssignModalOpened, { open: openAutoAssignModal, close: closeAutoAssignModal }] = useDisclosure(false);

    const [payment, setPayment] = useState<Partial<Payment>>({
        attachments: [],
        retentions: [],
        retained_amount: 0,
        notes: '',
        reservations: [],
        service_bookings: [],
    });

    const [isUploading, setIsUploading] = useState(false);
    const [uploadFile, setUploadFile] = useState<File | null>(null);

    // Populate data when loaded
    useEffect(() => {
        if (paymentData?.data) {
            setPayment(paymentData.data);
        }
    }, [paymentData]);

    const handleSaveUpdate = (updatedFields: Partial<Payment>) => {
        const updatedPayment = { ...payment, ...updatedFields };
        setPayment(updatedPayment);

        const payload: Record<string, any> = {
            id: updatedPayment.id,
            amount: updatedPayment.amount,
            currency: updatedPayment.currency,
            payment_method: updatedPayment.payment_method,
            payment_date: updatedPayment.payment_date,
            due_date: updatedPayment.due_date,
            status: updatedPayment.status,
            notes: updatedPayment.notes,
            attachments: updatedPayment.attachments,
            from_person_id: updatedPayment.from_person_id,
            from_business_id: updatedPayment.from_business_id,
            to_person_id: updatedPayment.to_person_id,
            to_business_id: updatedPayment.to_business_id,
            from_name: updatedPayment.from_name,
            to_name: updatedPayment.to_name,
            expected_amount: updatedPayment.expected_amount,
            refunded_amount: updatedPayment.refunded_amount,
            retained_amount: updatedPayment.retained_amount,
            retentions: updatedPayment.retentions,
        };

        if (updatedFields.affected_reservations !== undefined) {
            payload.affected_reservations = updatedFields.affected_reservations;
        } else if (updatedPayment.reservations) {
            payload.affected_reservations = updatedPayment.reservations.map(r => ({
                id: r.id,
                amount: r.PaymentReservation?.amount ?? r.payment_reservation?.amount ?? 0
            }));
        }

        if (updatedFields.affected_service_bookings !== undefined) {
            payload.affected_service_bookings = updatedFields.affected_service_bookings;
        } else if (updatedPayment.service_bookings) {
            payload.affected_service_bookings = updatedPayment.service_bookings.map(b => ({
                id: b.id,
                amount: b.PaymentServiceBooking?.amount ?? b.payment_service_booking?.amount ?? 0
            }));
        }

        updatePaymentMutation.mutate(payload);
    };

    const handleAddReservation = (resId: number, amount: number) => {
        const currentReservations = payment.reservations || [];
        if (currentReservations.some(r => r.id === resId)) {
            notify.error(t('error'), t('reservation_already_linked'));
            return;
        }

        const updatedReservations = [
            ...currentReservations,
            {
                id: resId,
                payment_reservation: { amount },
                PaymentReservation: { amount }
            }
        ];
        const affected = updatedReservations.map(r => ({
            id: r.id,
            amount: r.PaymentReservation?.amount ?? r.payment_reservation?.amount ?? 0
        }));
        handleSaveUpdate({ reservations: updatedReservations, affected_reservations: affected });
    };

    const handleUnlinkReservation = (resId: number) => {
        const updatedReservations = (payment.reservations || []).filter(r => r.id !== resId);
        const affected = updatedReservations.map(r => ({
            id: r.id,
            amount: r.PaymentReservation?.amount ?? r.payment_reservation?.amount ?? 0
        }));
        handleSaveUpdate({ reservations: updatedReservations, affected_reservations: affected });
    };

    const handleReservationAmountChange = (resId: number, newAmount: number) => {
        const updatedReservations = (payment.reservations || []).map(r => {
            if (r.id === resId) {
                return {
                    ...r,
                    payment_reservation: {
                        ...(r.payment_reservation || r.PaymentReservation || {}),
                        amount: newAmount
                    },
                    PaymentReservation: {
                        ...(r.PaymentReservation || r.payment_reservation || {}),
                        amount: newAmount
                    }
                };
            }
            return r;
        });
        const affected = updatedReservations.map(r => ({
            id: r.id,
            amount: r.PaymentReservation?.amount ?? r.payment_reservation?.amount ?? 0
        }));
        handleSaveUpdate({ reservations: updatedReservations, affected_reservations: affected });
    };

    const handleAddServiceBooking = (booking: ServiceBooking, amount: number) => {
        const currentBookings = payment.service_bookings || [];
        if (currentBookings.some(b => b.id === booking.id)) {
            notify.error(t('error'), t('booking_already_linked'));
            return;
        }

        const updatedBookings = [
            ...currentBookings,
            {
                ...booking,
                payment_service_booking: { amount },
                PaymentServiceBooking: { amount }
            }
        ];
        const affected = updatedBookings.map(b => ({
            id: b.id,
            amount: b.PaymentServiceBooking?.amount ?? b.payment_service_booking?.amount ?? 0
        }));
        handleSaveUpdate({ service_bookings: updatedBookings, affected_service_bookings: affected });
    };

    const handleUnlinkServiceBooking = (bookingId: number) => {
        const updatedBookings = (payment.service_bookings || []).filter(b => b.id !== bookingId);
        const affected = updatedBookings.map(b => ({
            id: b.id,
            amount: b.PaymentServiceBooking?.amount ?? b.payment_service_booking?.amount ?? 0
        }));
        handleSaveUpdate({ service_bookings: updatedBookings, affected_service_bookings: affected });
    };

    const handleServiceBookingAmountChange = (bookingId: number, newAmount: number) => {
        const updatedBookings = (payment.service_bookings || []).map(b => {
            if (b.id === bookingId) {
                return {
                    ...b,
                    payment_service_booking: {
                        ...(b.payment_service_booking || b.PaymentServiceBooking || {}),
                        amount: newAmount
                    },
                    PaymentServiceBooking: {
                        ...(b.PaymentServiceBooking || b.payment_service_booking || {}),
                        amount: newAmount
                    }
                };
            }
            return b;
        });
        const affected = updatedBookings.map(b => ({
            id: b.id,
            amount: b.PaymentServiceBooking?.amount ?? b.payment_service_booking?.amount ?? 0
        }));
        handleSaveUpdate({ service_bookings: updatedBookings, affected_service_bookings: affected });
    };

    const handleUpload = async (file: File | null) => {
        if (!file) {
            setUploadFile(null);
            return;
        }
        setUploadFile(file);
        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('image', file);
            const res = await api.upload<{ url: string, key: string }>('/payments/upload-attachment', formData);

            if (res.error) {
                notify.error(t('error'), typeof res.error === 'string' ? res.error : (res.error?.message || 'Upload failed'));
                setUploadFile(null);
            } else if (res.data) {
                const newAttachments = [...(payment.attachments || []), res.data.url];
                handleSaveUpdate({ attachments: newAttachments });
                setUploadFile(null);
            }
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Upload failed';
            notify.error(t('error'), msg);
            setUploadFile(null);
        } finally {
            setIsUploading(false);
        }
    };

    const removeAttachment = (index: number) => {
        const newAttachments = [...(payment.attachments || [])];
        newAttachments.splice(index, 1);
        handleSaveUpdate({ attachments: newAttachments });
    };

    const handleDelete = () => {
        if (!payment.id) return;
        deletePaymentMutation.mutate(payment.id, {
            onSuccess: () => {
                const ownedBusinessIds = localTenant()?.businesses?.map(b => b.id) || [];
                const isMoneyIn = ownedBusinessIds.includes(payment.to_business_id || -1);
                if (isMoneyIn) {
                    navigate(PAYMENTS_INCOME_PATH);
                } else {
                    navigate(PAYMENTS_OUTCOME_PATH);
                }
            }
        });
    };

    const isMoneyIn = useMemo(() => {
        const ownedBusinessIds = localTenant()?.businesses?.map(b => b.id) || [];
        return ownedBusinessIds.includes(payment.to_business_id || -1);
    }, [payment.to_business_id]);

    useEffect(() => {
        if (payment.id) {
            setWebTitle(`${isMoneyIn ? t('payment_from_client') : t('payment_to_provider')} #${payment.id}`);
        }
    }, [isMoneyIn, payment.id, t]);

    // Calculate linked totals and unallocated balance
    const totalLinkedAmount = useMemo(() => {
        if (isMoneyIn) {
            return (payment.reservations || []).reduce((sum, r) => {
                const linkAmt = r.PaymentReservation?.amount ?? r.payment_reservation?.amount ?? 0;
                return sum + Number(linkAmt || 0);
            }, 0);
        } else {
            return (payment.service_bookings || []).reduce((sum, b) => {
                const linkAmt = b.PaymentServiceBooking?.amount ?? b.payment_service_booking?.amount ?? 0;
                return sum + Number(linkAmt || 0);
            }, 0);
        }
    }, [isMoneyIn, payment.reservations, payment.service_bookings]);

    const totalPaymentAmount = Number(payment.amount || 0);
    const unallocatedAmount = totalPaymentAmount - totalLinkedAmount;
    const defaultLinkAmount = Math.max(0, unallocatedAmount > 0 ? unallocatedAmount : totalPaymentAmount);
    const providerId = payment.to_business_id || payment.from_business_id;
    const itemsCount = isMoneyIn ? (payment.reservations || []).length : (payment.service_bookings || []).length;

    if (isLoadingPayment) {
        return (
            <div>
                <PageHeader title={`${t('payment')} #${id}`} />
                <div className="panel-content">
                    <SkeletonList />
                </div>
            </div>
        );
    }

    const backPath = isMoneyIn ? PAYMENTS_INCOME_PATH : PAYMENTS_OUTCOME_PATH;

    return (
        <div>

            {/* Main content grid */}
            <div className="max-w-6xl mx-auto px-4">
                {/* Minimal Top Header */}
                <div className="flex items-center gap-2.5 mb-3">
                    <Link
                        to={backPath}
                        className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                        title={isMoneyIn ? t('payments_income') : t('payments_outcome')}
                    >
                        <IconArrowLeft size={18} />
                    </Link>

                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-wider ${isMoneyIn
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : 'bg-rose-50 text-rose-700 border border-rose-200'
                        }`}>
                        {isMoneyIn ? (
                            <IconArrowDownLeft size={15} stroke={2.5} />
                        ) : (
                            <IconArrowUpRight size={15} stroke={2.5} />
                        )}
                        {isMoneyIn ? t('payment_from_client') : t('payment_to_provider')}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">

                    {/* LEFT COLUMN */}
                    <div className="space-y-2">
                        {payment.status === 'pending' && (
                            <div className="card shadow-2xs border-yellow-200 bg-yellow-50/30 p-4 space-y-3 rounded-xl">
                                <div className="text-xs font-semibold text-yellow-900">
                                    {t('pending_payment_request')}
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        size="xs"
                                        color="green"
                                        className="flex-1"
                                        onClick={() => {
                                            handleSaveUpdate({
                                                status: 'paid',
                                                payment_date: new Date().toISOString().split('T')[0]
                                            });
                                        }}
                                    >
                                        {t('mark_as_paid')}
                                    </Button>
                                    <Button
                                        size="xs"
                                        color="gray"
                                        className="flex-1"
                                        variant="outline"
                                        onClick={() => {
                                            handleSaveUpdate({ status: 'cancelled' });
                                        }}
                                    >
                                        {t('cancel_request')}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Financial Hero Card */}
                        <div className="card shadow-sm border border-slate-200/80 rounded-2xl bg-white overflow-hidden">
                            <div className="p-6 space-y-5">
                                {/* Payer to Payee flow */}
                                <div className="flex items-center justify-between text-xs p-3.5 bg-slate-50/80 rounded-xl border border-slate-100">
                                    <div className="flex flex-col flex-1 min-w-0">
                                        <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">{t('origin')}</span>
                                        <Link
                                            to={`${BUSINESSES_PATH}/${payment.from_business_id}`}
                                            className="text-lg font-bold text-blue-600 hover:text-blue-800 hover:underline mt-0.5">
                                            {payment.from_name}
                                        </Link>
                                    </div>

                                    <div className={`px-3 shrink-0 ${isMoneyIn ? 'text-emerald-500' : 'text-rose-500'}`}>
                                        <IconArrowRight size={16} />
                                    </div>

                                    <div className="flex flex-col flex-1 min-w-0 text-right">
                                        <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">{t('recipient')}</span>
                                        <Link
                                            to={`${BUSINESSES_PATH}/${payment.to_business_id}`}
                                            className="text-lg font-bold text-blue-600 hover:text-blue-800 hover:underline mt-0.5">
                                            {payment.to_name}
                                        </Link>
                                    </div>
                                </div>

                                {/* Main Amount */}
                                <div className="space-y-4">
                                    {payment.expected_amount !== null && Number(payment.expected_amount) > 0 ? (
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="text-center py-3.5 bg-slate-50/60 rounded-xl border border-slate-100">
                                                <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">
                                                    {t('requested_amount')}
                                                </div>
                                                <div className="text-lg font-extrabold text-slate-900">
                                                    {formatPrice(Number(payment.expected_amount || 0), { currency: payment.currency })}
                                                    <span className="text-xs text-slate-400 font-semibold ml-1 uppercase">
                                                        {payment.currency}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className={`text-center py-3.5 rounded-xl border ${isMoneyIn ? 'bg-emerald-50/30 border-emerald-100' : 'bg-rose-50/30 border-rose-100'
                                                }`}>
                                                <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">
                                                    {t('paid_amount')}
                                                </div>
                                                <div className={`text-lg font-extrabold ${isMoneyIn ? 'text-emerald-950' : 'text-rose-950'}`}>
                                                    {formatPrice(Number(payment.amount || 0), { currency: payment.currency })}
                                                    <span className="text-xs text-slate-400 font-semibold ml-1 uppercase">
                                                        {payment.currency}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className={`text-center py-4 rounded-xl border ${isMoneyIn ? 'bg-emerald-50/20 border-emerald-100/60' : 'bg-rose-50/20 border-rose-100/60'
                                            }`}>
                                            <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1">
                                                {t('amount')}
                                            </div>
                                            <div className={`text-3xl font-extrabold ${isMoneyIn ? 'text-emerald-950' : 'text-rose-950'}`}>
                                                {formatPrice(Number(payment.amount || 0), { currency: payment.currency })}
                                                <span className="text-sm text-slate-400 font-semibold ml-1.5 uppercase">
                                                    {payment.currency}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Financial parameters (Date & Method) */}
                                <div className="divide-y divide-slate-100 text-sm border-t border-slate-100 pt-2">
                                    {payment.status === 'pending' ? (
                                        <div className="py-2.5 flex flex-col gap-1">
                                            <span className="text-slate-500 font-semibold text-xs uppercase tracking-wider">{t('due_date')}</span>
                                            <DateInput
                                                value={payment.due_date}
                                                onChange={(date: string | null) => handleSaveUpdate({ due_date: date })}
                                                size="xs"
                                                required
                                            />
                                        </div>
                                    ) : (
                                        <div className="py-2.5 flex justify-between items-center">
                                            <span className="text-slate-500 font-semibold text-xs uppercase tracking-wider">{t('payment_date')}</span>
                                            <span className="font-bold text-slate-800">
                                                {payment.payment_date ? formatDayLong(payment.payment_date) : '--'}
                                            </span>
                                        </div>
                                    )}

                                    <div className="py-2.5 flex justify-between items-center">
                                        <span className="text-slate-500 font-semibold text-xs uppercase tracking-wider">{t('method')}</span>
                                        <span className="font-bold text-slate-800 capitalize">
                                            {payment.payment_method ? t(`payment_methods.${payment.payment_method}`) : '--'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Retentions Card */}
                        <div className="card shadow-sm border border-slate-200/80 rounded-2xl bg-white overflow-hidden p-5">
                            <PaymentRetentionsSection
                                retentions={payment.retentions || []}
                                totalAmount={Number(payment.amount || 0)}
                                currency={payment.currency}
                                onChange={(retentions, retainedAmount) =>
                                    handleSaveUpdate({
                                        retentions,
                                        retained_amount: retainedAmount,
                                    })
                                }
                            />
                        </div>

                        {/* Notes Card */}
                        <div className="card shadow-sm border border-slate-200/80 rounded-2xl bg-white overflow-hidden">
                            <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                                <span className="font-bold text-slate-800 text-xs tracking-wide uppercase flex items-center gap-1.5">
                                    <IconNotes size={14} className="text-slate-500" />
                                    {t('notes')}
                                </span>
                                <Button
                                    size="xs"
                                    variant="subtle"
                                    color="blue"
                                    onClick={openNotesModal}
                                    leftSection={payment.notes ? <IconPencil size={13} /> : <IconPlus size={13} />}
                                >
                                    {t('edit_note')}
                                </Button>
                            </div>
                            <div className="p-5">
                                {payment.notes}
                            </div>
                        </div>

                        {/* Attachments Card */}
                        <div className="card shadow-sm border border-slate-200/80 rounded-2xl bg-white overflow-hidden">
                            <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                                <span className="font-bold text-slate-800 text-xs tracking-wide uppercase">
                                    {t('attachments')}
                                </span>
                            </div>
                            <div className="p-5 space-y-4">
                                <FileInput
                                    placeholder={t('upload')}
                                    accept="image/png,image/jpeg,application/pdf"
                                    value={uploadFile}
                                    onChange={handleUpload}
                                    leftSection={<IconUpload size={15} />}
                                    disabled={isUploading}
                                    size="xs"
                                />
                                {isUploading && <Loader size="sm" mt="sm" />}

                                {(payment.attachments && payment.attachments.length > 0) ? (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                        {payment.attachments.map((url, index) => {
                                            const isPdf = url.toLowerCase().endsWith('.pdf') || url.includes('/download') || url.includes('/vouchers/');
                                            return (
                                                <div key={index} className="relative group bg-slate-50 rounded-xl overflow-hidden border border-slate-200 aspect-square hover:shadow-md transition-shadow">
                                                    {isPdf ? (
                                                        <a
                                                            href={url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="w-full h-full flex flex-col items-center justify-center p-3 hover:bg-slate-100 transition-colors"
                                                        >
                                                            <IconFileText size={36} className="text-red-500 mb-1" />
                                                            <span className="text-[10px] text-slate-500 font-bold uppercase">PDF</span>
                                                        </a>
                                                    ) : (
                                                        <a
                                                            href={url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="w-full h-full block"
                                                        >
                                                            <img
                                                                src={url}
                                                                alt={`Attachment ${index + 1}`}
                                                                className="w-full h-full object-cover hover:scale-[1.02] transition-transform"
                                                            />
                                                        </a>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => removeAttachment(index)}
                                                        className="absolute top-1.5 right-1.5 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 shadow-sm cursor-pointer"
                                                        title={t('remove')}
                                                    >
                                                        <IconX size={12} />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-xs text-slate-400 italic py-1">
                                        {t('no_attachments')}
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>

                    {/* RIGHT COLUMN */}
                    <div className="space-y-2">
                        {/* Linked Items Card */}
                        <div className="card shadow-sm border border-slate-200/80 rounded-2xl bg-white overflow-hidden">
                            <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between gap-4">
                                <div className="flex items-center gap-2">
                                    <span className="font-bold text-slate-800 text-xs tracking-wide uppercase">
                                        {t('reservations')}
                                    </span>
                                    {itemsCount > 0 && (
                                        <Badge size="xs" color="gray" variant="light">
                                            {itemsCount}
                                        </Badge>
                                    )}
                                </div>
                                <div className="flex items-center gap-1.5">
                                    {unallocatedAmount > 0 && (
                                        <Button
                                            size="xs"
                                            variant="light"
                                            color="amber"
                                            leftSection={<IconWand size={14} />}
                                            onClick={openAutoAssignModal}
                                            className="font-semibold"
                                        >
                                            {t('auto_assign')}
                                        </Button>
                                    )}
                                    <Button
                                        size="xs"
                                        variant="light"
                                        color="blue"
                                        onClick={isMoneyIn ? openLinkModal : openLinkCostModal}
                                    >
                                        <IconLinkPlus size={16} />
                                    </Button>
                                </div>
                            </div>

                            <div className="p-5 space-y-4">
                                {/* Allocation Summary Bar */}
                                {itemsCount > 0 && (
                                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/70 flex items-center justify-between text-xs">
                                        <div className="flex items-center gap-2">
                                            <span className="text-slate-500 font-semibold">{t('total_applied')}:</span>
                                            <span className="font-bold text-slate-800">
                                                ${formatPrice(totalLinkedAmount)} {payment.currency}
                                            </span>
                                        </div>

                                        {unallocatedAmount === 0 ? (
                                            <Badge color="green" size="sm" variant="light">
                                                {t('fully_allocated')}
                                            </Badge>
                                        ) : unallocatedAmount > 0 ? (
                                            <Badge color="amber" size="sm" variant="light">
                                                {t('unallocated_balance')}: ${formatPrice(unallocatedAmount)}
                                            </Badge>
                                        ) : (
                                            <Badge color="red" size="sm" variant="light">
                                                {t('over_allocated')}: ${formatPrice(Math.abs(unallocatedAmount))}
                                            </Badge>
                                        )}
                                    </div>
                                )}

                                {/* Linked list */}
                                {isMoneyIn ? (
                                    <div className="space-y-2">
                                        {payment.reservations.map(r => (
                                            <LinkedReservationRow
                                                key={r.id}
                                                reservation={r}
                                                currency={payment.currency}
                                                onAmountChange={handleReservationAmountChange}
                                                onUnlink={handleUnlinkReservation}
                                            />
                                        ))}
                                    </div>
                                ) :
                                    <div className="space-y-2">
                                        {payment.service_bookings.map(b => (
                                            <LinkedServiceBookingRow
                                                key={b.id}
                                                booking={b}
                                                currency={payment.currency}
                                                onAmountChange={handleServiceBookingAmountChange}
                                                onUnlink={handleUnlinkServiceBooking}
                                            />
                                        ))}
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
                <div className='px-2 pt-12 text-center'>
                    <Button
                        color="red"
                        variant="subtle"
                        onClick={openDeleteModal}
                        leftSection={<IconTrash size={15} />}
                        size="xs"
                    >
                        {t('delete')}
                    </Button>
                </div>
                <ShowMetadata item={paymentData?.data} />
            </div>

            {/* Link Reservation Modal */}
            <LinkReservationModal
                opened={linkModalOpened}
                onClose={closeLinkModal}
                defaultAmount={defaultLinkAmount}
                currency={payment.currency}
                existingIds={(payment.reservations || []).map(r => r.id)}
                onConfirm={handleAddReservation}
            />

            {/* Link Provider Cost Modal */}
            <LinkServiceBookingCostModal
                opened={linkCostModalOpened}
                onClose={closeLinkCostModal}
                providerId={providerId}
                defaultAmount={defaultLinkAmount}
                currency={payment.currency}
                existingIds={(payment.service_bookings || []).map(b => b.id)}
                onConfirm={handleAddServiceBooking}
            />

            {/* Edit Notes Modal */}
            <EditNotesModal
                opened={notesModalOpened}
                onClose={closeNotesModal}
                currentNotes={payment.notes || ''}
                onSave={(newNotes) => handleSaveUpdate({ notes: newNotes })}
            />

            {/* Delete Modal */}
            <Modal opened={deleteModalOpened} onClose={closeDeleteModal} title={t('delete_payment')} centered>
                <Text size="sm">{t('are_you_sure_delete_payment')}</Text>
                <div className="flex justify-between mt-8">
                    <Button variant="default" onClick={closeDeleteModal} size="xs">
                        {t('cancel')}
                    </Button>
                    <Button color="red" onClick={handleDelete} loading={deletePaymentMutation.isPending} size="xs">
                        {t('confirm')}
                    </Button>
                </div>
            </Modal>

            {/* Auto Assign Modal */}
            <AutoAssignPaymentsModal
                opened={autoAssignModalOpened}
                onClose={closeAutoAssignModal}
                paymentId={payment.id ? Number(payment.id) : undefined}
            />
        </div>
    );
}
